import { useContext, useEffect, useState } from 'react';
import Tree from 'react-d3-tree';
import { Offcanvas, Form, Row, Col, InputGroup, Button, Spinner } from 'react-bootstrap';
import { BsEnvelopeFill, BsPersonFill, BsBriefcaseFill, BsBuilding } from 'react-icons/bs';
import { transformHierarchyToBackend } from '..';
import { dynamicRegisterForm } from '../../../api/authApi';
import ReactNotifications, { showApiErrors } from '../reactNotifications/ReactNotifications';
import * as utilConstants from '../../../utils/constants/utilsConstants';
import OrgNode from './OrgNode';
import { useNavigate } from 'react-router-dom';
import { updateHierarchy } from '../../../api/companyStructureApi/companyStructureApi';
import Context from '../../../context/Context';

const initialData = {
    first_name: "",
    last_name: "",
    email: "",
    role: "Company Head",
    department: "",
    img: "https://cdn-icons-png.flaticon.com/512/847/847969.png",
    manual: false,
    children: []
};
const CustomOrgChart = ({
    title = '',
    formValues = {},
    setStep = () => { },
    isAddEdit = true,
    singleCompanyHierarchy,
    members,
    minHeight = '250px',
    id = null
}) => {

    const [data, setData] = useState(singleCompanyHierarchy || initialData);
    const [selectedNode, setSelectedNode] = useState(null);
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editDepartment, setEditDepartment] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editRole, setEditRole] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const navigate = useNavigate();
    const { user } = useContext(Context);


    const backendPayload = transformHierarchyToBackend(data);

    const handleSubmit = (isHierarchy) => {
        localStorage.setItem("company_hierarchy_backup", JSON.stringify(data));
        const cleanMembers = members.map(({ role, ...rest }) => rest);
        let payload;

        if (isHierarchy) {
            payload = {
                ...formValues,
                mode: title === "Business Structure" ? "Self Serve" : "Managed",
                company_head: {
                    first_name: formValues?.first_name,
                    last_name: formValues?.last_name,
                    email: formValues?.email,
                    phone_number: formValues?.phone_number
                },
                ...backendPayload,
                members: cleanMembers.slice(1)
            };
        } else {
            payload = {
                ...formValues,
                mode: title === "Business Structure" ? "Self Serve" : "Managed",
                company_head: {
                    first_name: formValues?.first_name,
                    last_name: formValues?.last_name,
                    email: formValues?.email,
                    phone_number: formValues?.phone_number
                },
                members: cleanMembers.slice(1),
            };
        }
        dynamicRegisterForm(payload, (res) => {
            if (res?.status === true || res?.success) {
                ReactNotifications(
                    res?.message || res?.data?.message,
                    utilConstants.sucessState200
                );
                setData(initialData);
                localStorage.removeItem("company_hierarchy_backup");
                navigate('/login')
            } else {
                showApiErrors(res);
            }
        });
    }

    const handleHierarchyUpdate = () => {
        if (id) {
            let payload = {
                ...backendPayload,
            };

            updateHierarchy(id, payload, (res) => {
                if (res?.status === true || res?.success) {
                    ReactNotifications(
                        res?.message || res?.data?.message,
                        utilConstants.sucessState200
                    );
                } else {
                    showApiErrors(res);
                }
            });
        }
    }

    const updateNode = (targetId, targetDept, newData, node = data) => {
        if (node.email === targetId && node.department === targetDept) {
            Object.assign(node, newData);
        } else if (node.children) {
            node.children.forEach((child) => updateNode(targetId, targetDept, newData, child));
        }
    };

    const addNode = (parentId, parentDept, newNode, node = data) => {
        if (node.email === parentId && node.department === parentDept) {
            if (!node.children) node.children = [];
            node.children.push(newNode);
        } else if (node.children) {
            node.children.forEach((child) => addNode(parentId, parentDept, newNode, child));
        }
    };

    const openEdit = (nodeDatum) => {
        setSelectedNode(nodeDatum);
        setEditFirstName(nodeDatum.first_name || '');
        setEditLastName(nodeDatum.last_name || '');
        setEditDepartment(nodeDatum.department || '');
        setEditEmail(nodeDatum.email || '');
        setEditRole(nodeDatum.role || '');
        setIsAdding(false);
    };

    const openAdd = (nodeDatum) => {
        setSelectedNode(nodeDatum);
        if (nodeDatum?.role === "Team Head") {
            setEditDepartment(nodeDatum?.department);
            setEditRole("Member");
        } else {
            setEditDepartment('');
            setEditRole('');
        }
        setEditFirstName('');
        setEditLastName('');
        setEditEmail('');
        setIsAdding(true);
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();

        const newData = { ...data };
        const selectedMember = members.find(m => m.email === editEmail && m.department === editDepartment);
        const nodeData = {
            first_name: selectedMember?.first_name || editFirstName,
            last_name: selectedMember?.last_name || editLastName,
            department: editDepartment,
            email: editEmail,
            role: editRole,
            img: 'https://cdn-icons-png.flaticon.com/512/847/847969.png',
            manual: isAdding ? true : selectedNode?.manual ?? false,
        };

        if (isAdding) {
            addNode(selectedNode.email, selectedNode.department, nodeData, newData);
        } else {
            updateNode(selectedNode.email, selectedNode.department, nodeData, newData);
        }

        setData(newData);
        setSelectedNode(null);
        setIsAdding(false);
        setEditFirstName('');
        setEditLastName('');
        setEditDepartment('');
        setEditEmail('');
        setEditRole('');
    };

    useEffect(() => {
        if (members?.length > 0) {
            const owner = members.find((m) => m.role === "Company Head");

            if (owner) {
                setData((prev) => ({
                    ...prev,
                    first_name: owner.first_name,
                    last_name: owner.last_name,
                    email: owner.email,
                    role: "Company Head",
                    img: prev.img || "https://cdn-icons-png.flaticon.com/512/847/847969.png",
                    department: prev.department || "",
                    manual: prev.manual ?? false,
                    children: prev.children || []
                }));
            }
        }
    }, [members]);

    useEffect(() => {
        if (singleCompanyHierarchy) {
            setData(singleCompanyHierarchy);
        }

    }, [singleCompanyHierarchy])


    const deleteNode = (targetId, targetDept, node = data) => {
        if (!node?.children) return;
        node.children = node.children.filter(
            (child) => !(child.email === targetId && child.department === targetDept));
        node.children.forEach((child) => deleteNode(targetId, targetDept, child));
    };

    const isRoot = selectedNode?.email === data.email;

    useEffect(() => {
        setDrawerLoading(true);
        const timer = setTimeout(() => setDrawerLoading(false), 2000);
        return () => clearTimeout(timer);

    }, [selectedNode]);

    console.log(data, singleCompanyHierarchy, "data")

    return (
        <div className="org-container" style={{ height: `calc(100vh - ${minHeight})` }}>
            <div className="org-chart">
                <Tree
                    data={data}
                    translate={{ x: 650.2375, y: 100.5 }}
                    nodeSize={{ x: 300, y: 200 }}
                    orientation="vertical"
                    renderCustomNodeElement={(rd) => (
                        <OrgNode
                            {...rd}
                            selectedNode={selectedNode}
                            setSelectedNode={setSelectedNode}
                            data={data}
                            setData={setData}
                            openAdd={openAdd}
                            openEdit={openEdit}
                            deleteNode={deleteNode}
                            isAddEdit={isAddEdit}
                        />
                    )}
                    zoomable
                    separation={{ siblings: 1, nonSiblings: 1 }}
                    pathFunc="diagonal"
                    styles={{
                        links: {
                            stroke: '#ffffff',
                            strokeWidth: 2,
                        },
                    }}
                />
                {isAddEdit && <div className={`d-flex justify-content-${id ? `end` : `between`} gap-2 mx-3`}>
                    {!id && <div className='cursor-pointer'>
                        <p onClick={() => handleSubmit(false)} className='!text-black !font-medium fw-bold text-base ml-12 hover:border-b-2'>{`Skip this step & register>`}</p>
                    </div>}
                    <div className='d-flex justify-content-end gap-2 mx-4'>
                        {!id && <Button onClick={() => setStep(2)} variant='outline-light'
                            className="!text-[#000] border border-2 px-4 !font-medium">
                            Back
                        </Button>}
                        <button
                            className='font-medium'
                            onClick={id ? handleHierarchyUpdate : () => handleSubmit(true)}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#AEF468",
                                color: "#000",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                            }}
                        >
                            {!id ? `Finish Set Up` : `Update Hierarchy`}
                        </button>
                    </div>
                </div>}
            </div>

            <Offcanvas
                show={!!selectedNode}
                onHide={() => {
                    setSelectedNode(null);
                    setIsAdding(false);
                    setEditFirstName('');
                    setEditLastName('');
                    setEditDepartment('');
                    setEditEmail('');
                    setEditRole('');
                }}
                placement="end"
                backdrop
                scroll
                className="bg-light text-dark"
                style={{ width: 320 }}
            >
                <Offcanvas.Header closeButton closeVariant="dark">
                    <Offcanvas.Title className="fw-semibold">
                        {!isAddEdit ? 'Member Info' : isAdding ? 'Add New Member' : 'View/Update Info'}
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    {drawerLoading ? (
                        <div className="flex items-center justify-center h-100">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                        </div>
                    ) : (
                        <Form onSubmit={handleEditSubmit} autoComplete="off">
                            <Row className="g-3">
                                {isAdding ? (

                                    <Col xs={12}>
                                        <Form.Label className="text-uppercase small mb-1">Select Member</Form.Label>
                                        <Form.Select
                                            value={editEmail}
                                            onChange={(e) => {
                                                const selectedEmail = e.target.value;
                                                setEditEmail(selectedEmail);


                                                const selectedMember = members.find((m) => m.email === selectedEmail);
                                                if (selectedMember) {
                                                    setEditFirstName(selectedMember.first_name);
                                                    setEditLastName(selectedMember.last_name);
                                                }
                                            }}
                                            required
                                        >
                                            <option value="">Choose a member</option>
                                            {members?.filter((m) => m.role !== ("Company Head" || "Team Head"))
                                                .filter((m) => m.email !== selectedNode?.email)?.map((m, idx) => (
                                                    <option
                                                        key={idx}
                                                        value={m.email}
                                                    >
                                                        {m.first_name} {m.last_name} ({m.email})
                                                    </option>
                                                ))}
                                        </Form.Select>
                                    </Col>
                                ) : (

                                    <>
                                        <Col xs={6}>
                                            <Form.Label className="text-uppercase small mb-1">First Name</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><BsPersonFill /></InputGroup.Text>
                                                <Form.Control
                                                    placeholder="John"
                                                    value={editFirstName}
                                                    onChange={(e) => setEditFirstName(e.target.value)}
                                                    required
                                                    readOnly={!isAddEdit}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Label className="text-uppercase small mb-1">Last Name</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><BsPersonFill /></InputGroup.Text>
                                                <Form.Control
                                                    placeholder="Doe"
                                                    value={editLastName}
                                                    onChange={(e) => setEditLastName(e.target.value)}
                                                    required
                                                    readOnly={!isAddEdit}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="text-uppercase small mb-1">Email</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><BsEnvelopeFill /></InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="name@company.com"
                                                    value={editEmail}
                                                    onChange={(e) => setEditEmail(e.target.value)}
                                                    required
                                                    readOnly={!isAddEdit}
                                                />
                                            </InputGroup>
                                        </Col>
                                    </>
                                )}

                                {(!isRoot || isAdding) && (
                                    <>
                                        <Col xs={12}>
                                            <Form.Label className="text-uppercase small mb-1">Department</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><BsBuilding /></InputGroup.Text>
                                                <Form.Control
                                                    placeholder="Engineering"
                                                    value={

                                                        editDepartment
                                                    }
                                                    onChange={(e) => setEditDepartment(e.target.value)}
                                                    required
                                                    disabled={selectedNode?.role === "Member"}
                                                    readOnly={!isAddEdit}
                                                />
                                            </InputGroup>
                                        </Col>

                                        <Col xs={12}>
                                            <Form.Label className="text-uppercase small mb-1">Role</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <BsBriefcaseFill />
                                                </InputGroup.Text>
                                                <Form.Select
                                                    value={editRole}
                                                    onChange={(e) => setEditRole(e.target.value)}
                                                    required
                                                    disabled={!isAddEdit}
                                                >

                                                    <>
                                                        <option value="">Select Role</option>
                                                        <option value="Team Head">Team Head</option>
                                                        <option value="Member">Team Member</option>
                                                    </>

                                                </Form.Select>
                                            </InputGroup>
                                        </Col>

                                    </>
                                )}
                            </Row>

                            {isAddEdit && (
                                <div className="d-flex gap-2 mt-4">
                                    <Button type="submit" variant="dark" className="px-3">
                                        {isAdding ? 'Add Member' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="px-3 border"
                                        onClick={() => {
                                            setSelectedNode(null);
                                            setIsAdding(false);
                                            setEditFirstName('');
                                            setEditLastName('');
                                            setEditDepartment('');
                                            setEditEmail('');
                                            setEditRole('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form>
                    )}
                </Offcanvas.Body>

            </Offcanvas>
        </div>
    );
};

export default CustomOrgChart;
